import { logger } from "@/lib/logger";
import type Echo from "laravel-echo";

export interface EchoConnectionHandlers {
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export interface EchoConnectionState {
  isConnected: boolean;
  reconnectAttempts: number;
  timeout: ReturnType<typeof setTimeout> | null;
  connectionBound: boolean;
}

const globalState: EchoConnectionState = {
  isConnected: false,
  reconnectAttempts: 0,
  timeout: null,
  connectionBound: false,
};

const connectedListeners = new Set<() => void>();
const disconnectedListeners = new Set<() => void>();

function notifyConnected(): void {
  globalState.isConnected = true;
  globalState.reconnectAttempts = 0;
  connectedListeners.forEach((listener) => listener());
}

function notifyDisconnected(): void {
  globalState.isConnected = false;
  disconnectedListeners.forEach((listener) => listener());
}

function bindGlobalConnectionHandlers(echoRef: Echo<"reverb">): void {
  if (globalState.connectionBound) {
    return;
  }

  echoRef.connector.pusher.connection.bind("connected", () => {
    logger.debug("🔌 Echo connected");
    notifyConnected();
  });

  echoRef.connector.pusher.connection.bind("disconnected", () => {
    logger.debug("❌ Echo disconnected");
    notifyDisconnected();
  });

  echoRef.connector.pusher.connection.bind("error", (err: unknown) => {
    logger.error("Echo connection error", err);
  });

  globalState.connectionBound = true;
}

export function subscribeEchoConnection(
  handlers: EchoConnectionHandlers,
): () => void {
  if (handlers.onConnected) {
    connectedListeners.add(handlers.onConnected);
  }

  if (handlers.onDisconnected) {
    disconnectedListeners.add(handlers.onDisconnected);
  }

  return () => {
    if (handlers.onConnected) {
      connectedListeners.delete(handlers.onConnected);
    }

    if (handlers.onDisconnected) {
      disconnectedListeners.delete(handlers.onDisconnected);
    }
  };
}

export function ensureEchoConnectionBound(
  echoRef: Echo<"reverb"> | null,
): void {
  if (echoRef) {
    bindGlobalConnectionHandlers(echoRef);
  }
}

export function isEchoConnected(echoRef: Echo<"reverb"> | null): boolean {
  if (!echoRef) {
    return false;
  }

  return echoRef.connector.pusher.connection.state === "connected";
}

export function getEchoConnectionState(): EchoConnectionState {
  return globalState;
}

/** @deprecated Use subscribeEchoConnection instead */
export function bindEchoConnectionHandlers(
  echoRef: Echo<"reverb"> | null,
  state: EchoConnectionState,
  handlers: EchoConnectionHandlers,
  label = "Echo",
): void {
  ensureEchoConnectionBound(echoRef);
  subscribeEchoConnection(handlers);
}

/** @deprecated Global handlers are no longer unbound individually */
export function unbindEchoConnectionHandlers(
  _echoRef: Echo<"reverb"> | null,
  state: EchoConnectionState,
): void {
  state.connectionBound = false;
}
